// legalData.js — content for the Privacy Policy, Terms of Service, and Cookie
// Policy pages. Rendered by pages/LegalPage.jsx. Plain data so the legal copy
// can be edited without touching JSX.
//
// Each document: { slug, title, eyebrow, updated, intro, sections[] }
// Each section: { heading, body?: string[], list?: string[] }

const COMPANY = "Lumotis";
const CONTACT_EMAIL = "hello@lumotis.com";
const PRIVACY_EMAIL = "privacy@lumotis.com";
const SITE = "lumotis.com";
const EFFECTIVE = "June 1, 2026";

export const LEGAL_DOCS = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Legal",
    updated: EFFECTIVE,
    intro: `${COMPANY} ("we", "us", or "our") respects your privacy. This Privacy Policy explains what information we collect when you use ${SITE} and our services, how we use it, and the choices you have. By using our website you agree to the practices described here.`,
    sections: [
      {
        heading: "1. Information We Collect",
        body: [
          "We collect information you provide directly to us and information collected automatically when you use our site.",
        ],
        list: [
          "Information you give us: name, email address, phone number, company name, website URL, and any message content you submit through our contact forms, audit tool, or newsletter signup.",
          "Audit tool inputs: the website URL you submit for analysis, plus the industry and goal you select. We fetch and analyze only publicly available pages at that URL.",
          "Automatic data: IP address, browser type, device information, referring pages, and pages viewed, collected via cookies and similar technologies.",
          "Usage analytics: aggregated, de-identified metrics about how visitors interact with our site.",
        ],
      },
      {
        heading: "2. How We Use Your Information",
        list: [
          "To respond to your inquiries and provide the services you request.",
          "To run the SEO audit you submit and email you the resulting report when you ask us to.",
          "To send marketing communications you have opted into (you can unsubscribe at any time).",
          "To improve our website, services, and customer experience.",
          "To detect, prevent, and address technical issues, fraud, or abuse.",
          "To comply with legal obligations.",
        ],
      },
      {
        heading: "3. Legal Bases for Processing (GDPR)",
        body: [
          "If you are in the European Economic Area or the UK, we process your personal data on the following legal bases: your consent; the performance of a contract with you; our legitimate interests in operating and improving our business; and compliance with legal obligations.",
        ],
      },
      {
        heading: "4. Sharing of Information",
        body: [
          "We do not sell your personal information. We may share it with trusted third parties only as needed to operate our business:",
        ],
        list: [
          "Service providers who host our site, send email, or provide analytics, bound by confidentiality obligations.",
          "Google PageSpeed Insights API, which we call to generate performance metrics for audited URLs.",
          "Authorities or other parties when required by law or to protect our rights.",
        ],
      },
      {
        heading: "5. Data Retention",
        body: [
          "We retain personal information for as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required by law. Audit submissions and lead records are kept until you ask us to delete them or they are no longer needed.",
        ],
      },
      {
        heading: "6. Your Rights",
        body: [
          "Depending on your location, you may have the right to access, correct, delete, or port your personal data, and to object to or restrict certain processing. You may also withdraw consent at any time.",
        ],
        list: [
          `To exercise any of these rights, email us at ${PRIVACY_EMAIL}.`,
          "We will respond within the timeframe required by applicable law.",
        ],
      },
      {
        heading: "7. Data Security",
        body: [
          "We use reasonable administrative, technical, and physical safeguards to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        heading: "8. International Transfers",
        body: [
          "We operate globally. Your information may be processed in countries other than your own. Where required, we use appropriate safeguards such as standard contractual clauses to protect transferred data.",
        ],
      },
      {
        heading: "9. Children's Privacy",
        body: [
          "Our services are not directed to children under 16, and we do not knowingly collect personal information from them.",
        ],
      },
      {
        heading: "10. Changes to This Policy",
        body: [
          "We may update this Privacy Policy from time to time. We will post the revised version on this page with an updated effective date.",
        ],
      },
      {
        heading: "11. Contact Us",
        body: [
          `If you have questions about this Privacy Policy or our data practices, contact us at ${PRIVACY_EMAIL} or ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    eyebrow: "Legal",
    updated: EFFECTIVE,
    intro: `These Terms of Service ("Terms") govern your access to and use of ${SITE} and the services provided by ${COMPANY}. By accessing our website or engaging our services, you agree to be bound by these Terms.`,
    sections: [
      {
        heading: "1. Use of Our Website",
        body: [
          "You may use our website for lawful purposes only. You agree not to misuse the site, interfere with its operation, attempt to gain unauthorized access, or use automated systems to scrape or overload our infrastructure.",
        ],
      },
      {
        heading: "2. The SEO Audit Tool",
        body: [
          "Our free audit tool analyzes publicly accessible pages at a URL you provide. By submitting a URL you confirm you are authorized to request analysis of that site.",
        ],
        list: [
          "Audit results are provided for informational purposes only and are generated automatically.",
          "We do not guarantee any specific ranking, traffic, or revenue outcome from acting on audit recommendations.",
          "Scores and findings are estimates and may change as search engines and third-party data sources update.",
        ],
      },
      {
        heading: "3. Services and Engagements",
        body: [
          "Paid services are governed by a separate written agreement or statement of work. In the event of a conflict between these Terms and a signed agreement, the signed agreement controls for that engagement.",
        ],
      },
      {
        heading: "4. Intellectual Property",
        body: [
          `All content on this website — including text, graphics, logos, and the ${COMPANY} name and brand — is owned by or licensed to us and protected by intellectual property laws. You may not copy, reproduce, or republish our content without prior written permission.`,
        ],
      },
      {
        heading: "5. Third-Party Links and Services",
        body: [
          "Our site may link to or rely on third-party websites and services that we do not control. We are not responsible for their content, policies, or practices.",
        ],
      },
      {
        heading: "6. Disclaimers",
        body: [
          'Our website and free tools are provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the site will be uninterrupted, error-free, or secure.',
        ],
      },
      {
        heading: "7. Limitation of Liability",
        body: [
          `To the maximum extent permitted by law, ${COMPANY} will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenue, arising from your use of the website or free tools.`,
        ],
      },
      {
        heading: "8. Indemnification",
        body: [
          `You agree to indemnify and hold harmless ${COMPANY} and its team from any claims, damages, or expenses arising out of your misuse of the website or violation of these Terms.`,
        ],
      },
      {
        heading: "9. Changes to These Terms",
        body: [
          "We may revise these Terms at any time. Continued use of the website after changes are posted constitutes acceptance of the updated Terms.",
        ],
      },
      {
        heading: "10. Governing Law",
        body: [
          "These Terms are governed by the laws applicable in our principal place of business, without regard to conflict-of-law principles.",
        ],
      },
      {
        heading: "11. Contact",
        body: [`Questions about these Terms? Email us at ${CONTACT_EMAIL}.`],
      },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    eyebrow: "Legal",
    updated: EFFECTIVE,
    intro: `This Cookie Policy explains how ${COMPANY} uses cookies and similar technologies on ${SITE}, and how you can control them.`,
    sections: [
      {
        heading: "1. What Are Cookies?",
        body: [
          "Cookies are small text files placed on your device when you visit a website. They help the site function, remember your preferences, and understand how visitors use the site.",
        ],
      },
      {
        heading: "2. Types of Cookies We Use",
        list: [
          "Essential cookies: required for the website to function, such as remembering your session and security.",
          "Preference cookies: remember your settings and choices to improve your experience.",
          "Analytics cookies: help us understand how visitors interact with the site so we can improve it. These are aggregated and de-identified.",
          "Marketing cookies: used to measure the effectiveness of campaigns and, where you have consented, to show relevant content.",
        ],
      },
      {
        heading: "3. Managing Cookies",
        body: [
          "You can control and delete cookies through your browser settings. Most browsers let you refuse or remove cookies; doing so may affect some features of the site. Where required, we ask for your consent to non-essential cookies before setting them.",
        ],
      },
      {
        heading: "4. Third-Party Cookies",
        body: [
          "Some cookies may be set by third-party services we use, such as analytics providers. We do not control these cookies; please review the relevant third party's privacy and cookie policies.",
        ],
      },
      {
        heading: "5. Changes to This Policy",
        body: [
          "We may update this Cookie Policy from time to time. The latest version will always be available on this page.",
        ],
      },
      {
        heading: "6. Contact",
        body: [
          `If you have questions about our use of cookies, email us at ${PRIVACY_EMAIL}.`,
        ],
      },
    ],
  },
};

export function getLegalDoc(slug) {
  return LEGAL_DOCS[slug] || null;
}
